import type { ReactNode } from 'react';
import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import type { loader as articlesLoader } from '@modules/professional/page/loaders/articles-page-loader.server';
import { PROFESSIONAL_TAB_ARTICLE_FILTERS_CONFIG } from '@modules/professional/config/professional-tab-filter-configs';
import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import type { AppliedFilterItem } from '~/modules/search/type/filter-state';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { ProfessionalTabListingBar } from './ProfessionalTabListingBar';
import { ProfessionalTabFilterSidebar } from './ProfessionalTabFilterSidebar';
import { ProfessionalAppliedFilters } from './ProfessionalAppliedFilters';
import { useInPageSearchTerm } from '../hooks/use-in-page-search-term';

interface ProfessionalArticlesTabLayoutProps {
    /** The desktop "Display: {n}" control. */
    display: ReactNode;
    /** The results: the grid or its empty state, and the paging controls. */
    children: ReactNode;
}

type ArticleSearchFilters = ReturnType<typeof useSearchFilters>;

/**
 * The chosen category as an applied filter. The shared article filter list
 * declares no category section, so the applied filters it reports never carry
 * one, and on this tab the category is the only filter there is.
 */
const getCategoryFilterItems = ({
    filterStates,
    updateFilter,
}: ArticleSearchFilters): AppliedFilterItem[] => {
    const state = filterStates.category;
    if (!state?.selectedCategory || state.isRoot) return [];
    return [
        {
            key: 'category',
            label: state.selectedCategory.name,
            action: () =>
                updateFilter('category', {
                    kind: 'CategorySelector',
                    selectedCategory: null,
                }),
        },
    ];
};

/**
 * The Articles tab's filter-and-search bar over its results, with the article
 * categories as the filter: a collapsible sidebar on desktop, the Refine sheet
 * on the phone. Choosing a category writes `categoryLink`, which the tab's
 * loader and its load-more requests both search on.
 */
export const ProfessionalArticlesTabLayout = ({
    display,
    children,
}: ProfessionalArticlesTabLayoutProps) => {
    const { filterStates, filterStatesRocco } =
        useLoaderData<typeof articlesLoader>();
    const { desktop } = useAppDisplayModeContext();
    // The site-wide `showFilter` cookie, shared with the other listing tabs.
    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();
    const [searchTerm, setSearchTerm] = useInPageSearchTerm();
    const searchFilters = useSearchFilters({
        category: 'blogpost',
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys: IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
        reportAppliedFiltersAs: 'professionalTab',
    });
    const appliedFilters = [
        ...getCategoryFilterItems(searchFilters),
        ...(searchFilters.appliedFilters ?? []),
    ];

    return (
        <CollapsiblePageLayout
            paddingClassName="px-0"
            disableMinHeight
            sidePanel={
                desktop &&
                filterStatesRocco && (
                    <div className="h-full md:pt-[1.8rem]">
                        <ProfessionalTabFilterSidebar
                            searchIndex="blogpost"
                            config={PROFESSIONAL_TAB_ARTICLE_FILTERS_CONFIG}
                            initialFilterStates={filterStatesRocco}
                            theme="light"
                        />
                    </div>
                )
            }
            hideSidePanel={!filterDrawerOpen}
        >
            {desktop ? (
                <ProfessionalTabListingBar
                    search={{
                        value: searchTerm,
                        onChange: setSearchTerm,
                        placeholder: 'Search Articles',
                    }}
                    filterDrawer={{
                        open: filterDrawerOpen,
                        onToggle: toggleFilterDrawer,
                    }}
                    filtersCount={appliedFilters.length}
                    display={display}
                />
            ) : (
                <SearchFilter
                    searchFilters={searchFilters}
                    filterDrawerOpen={filterDrawerOpen}
                    toggleFilterDrawer={toggleFilterDrawer}
                    searchBarVariant="revamp"
                    searchBarTheme="light"
                    hideSort
                    filterControl="plain"
                    searchPlaceholder="Search Articles"
                    mobileSearchPlaceholder="Search Articles"
                    appliedFiltersCount={appliedFilters.length}
                />
            )}
            {/* The phone's Refine badge and sheet already surface these. */}
            {desktop && appliedFilters.length > 0 && (
                <ProfessionalAppliedFilters
                    filterItems={appliedFilters}
                    theme="light"
                    // The category is this tab's only filter, so Clear all
                    // clears it too, where the Projects tab keeps its own.
                    onClearAll={() =>
                        searchFilters.resetFilters(
                            resetFilterStates(
                                searchFilters.filterStates,
                                IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
                            ),
                        )
                    }
                />
            )}
            {children}
        </CollapsiblePageLayout>
    );
};
